import {
  Component,
  DestroyRef,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  computed,
  inject,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ChartConfiguration } from 'chart.js';
import { TranslateService } from '@ngx-translate/core';
import { DataPlatformService } from '../../../data-platform/data-platform.service';
import {
  DataPlatformAudienceCountDailyPoint,
  DataPlatformAudienceDefinition,
  DataPlatformAudienceRule,
} from '../../../data-platform/data-platform.model';
import { ThemeService } from '../../../shared/services/theme.service';
import { DAILY_SUPPORTED_RULE_TYPES } from '../../../data-platform/audiences/audience-filter-to-rule.utils';

const DEFAULT_LOOKBACK_DAYS = 14;

@Component({
  selector: 'app-audience-trend-chart',
  templateUrl: './audience-trend-chart.component.html',
  styleUrls: ['./audience-trend-chart.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AudienceTrendChartComponent implements OnInit, OnChanges {
  /** Audience id (used in the request, returned in the response for sanity). */
  @Input() audienceId!: string;

  /** Site IDs the audience lives under. */
  @Input() siteIds!: number[];

  /**
   * Rules in the new data-platform shape. The component is responsible for
   * mapping these into a DataPlatformAudienceDefinition and calling
   * countAudienceDaily — the parent just provides the rule list.
   */
  @Input() rules: DataPlatformAudienceRule[] = [];

  /** Window size in days. Defaults to 14 to match the "last 2 weeks" design intent. */
  @Input() lookbackDays = DEFAULT_LOOKBACK_DAYS;

  /** `{page}` of the DataPlatform feature tag. */
  @Input({ required: true }) featurePage!: string;

  /** Multiplies each day's count; null keeps the counts exact. */
  @Input() set estimateShare(share: number | null) {
    this.share.set(share);
  }

  private readonly dataPlatformService = inject(DataPlatformService);
  private readonly translateService = inject(TranslateService);
  private readonly themeService = inject(ThemeService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly loading = signal<boolean>(false);
  protected readonly errored = signal<boolean>(false);
  protected readonly points = signal<DataPlatformAudienceCountDailyPoint[]>([]);

  private readonly share = signal<number | null>(null);
  private readonly isDark = signal<boolean>(false);

  protected readonly estimated = computed<boolean>(() => this.share() !== null);

  private readonly shownPoints = computed<DataPlatformAudienceCountDailyPoint[]>(() => {
    const share = this.share();
    const points = this.points();
    return share === null ? points : points.map((p) => ({ ...p, count: Math.round(p.count * share) }));
  });

  /**
   * True when the audience uses rule types whose semantics don't translate
   * cleanly into the daily aggregation server-side. The chart still renders
   * but with a "best-effort" badge.
   */
  protected readonly bestEffort = computed<boolean>(() =>
    this.rules.some((r) => !DAILY_SUPPORTED_RULE_TYPES.has(r.type.toLowerCase())),
  );

  protected readonly latestValue = computed<number | null>(() => {
    const pts = this.shownPoints();
    return pts.length ? pts[pts.length - 1].count : null;
  });

  protected readonly delta = computed<{ percent: number; up: boolean } | null>(() => {
    const pts = this.points();
    if (pts.length < 2) return null;
    const first = pts[0].count;
    const last = pts[pts.length - 1].count;
    if (first === 0) {
      return last === 0 ? { percent: 0, up: false } : { percent: 100, up: true };
    }
    const change = ((last - first) / first) * 100;
    return { percent: Math.abs(Math.round(change * 10) / 10), up: change >= 0 };
  });

  protected readonly chartConfig = computed<{
    data: ChartConfiguration<'line'>['data'];
    options: ChartConfiguration<'line'>['options'];
  } | null>(() => {
    const pts = this.shownPoints();
    if (!pts.length) return null;
    const dark = this.isDark();
    const gridColor = dark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)';
    const textColor = dark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.45)';
    const lineColor = '#1976d2';

    return {
      data: {
        labels: pts.map((p) => this.formatLabel(p.date)),
        datasets: [
          {
            data: pts.map((p) => p.count),
            borderColor: lineColor,
            backgroundColor: 'rgba(25, 118, 210, 0.12)',
            borderWidth: 2,
            tension: 0.3,
            pointRadius: 0,
            pointHoverRadius: 4,
            fill: true,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index' as const, intersect: false },
        plugins: {
          legend: { display: false },
          tooltip: { enabled: true },
        },
        scales: {
          x: {
            grid: { display: false },
            ticks: {
              font: { size: 10 },
              color: textColor,
              maxRotation: 0,
              autoSkip: true,
              maxTicksLimit: 6,
            },
          },
          y: {
            beginAtZero: true,
            grid: { color: gridColor },
            ticks: { font: { size: 10 }, color: textColor },
          },
        },
        animation: false,
      },
    };
  });

  ngOnInit(): void {
    this.themeService.isDarkThemeBS
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((dark) => this.isDark.set(dark ?? false));
    this.fetchTrend();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // ngOnInit does the first fetch.
    if (['audienceId', 'rules', 'siteIds', 'lookbackDays'].some((input) => changes[input]?.firstChange === false)) {
      this.fetchTrend();
    }
  }

  protected retry(): void {
    this.fetchTrend();
  }

  private fetchTrend(): void {
    if (!this.audienceId || !this.siteIds?.length) return;
    if (!this.rules?.length) {
      // No rules → nothing to count. Render empty without hitting the server.
      this.points.set([]);
      return;
    }

    const today = new Date();
    const start = new Date(today);
    start.setDate(start.getDate() - (this.lookbackDays - 1));

    const audience: DataPlatformAudienceDefinition = {
      id: this.audienceId,
      rules: this.rules,
    };

    this.loading.set(true);
    this.errored.set(false);
    this.dataPlatformService
      .countAudienceDaily(
        {
          siteIds: this.siteIds,
          audience,
          from: toIsoDate(start),
          to: toIsoDate(today),
        },
        `${this.featurePage}-trend-chart`,
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => {
          this.loading.set(false);
          if (result?.isPositive() && result.data) {
            this.points.set(result.data.points ?? []);
          } else {
            this.errored.set(true);
            this.points.set([]);
          }
        },
        error: () => {
          this.loading.set(false);
          this.errored.set(true);
          this.points.set([]);
        },
      });
  }

  private formatLabel(isoDate: string): string {
    const date = new Date(isoDate + 'T00:00:00');
    if (Number.isNaN(date.getTime())) {
      return isoDate;
    }
    const months: string[] = this.translateService.instant('time.months');
    return `${date.getDate()} ${months[date.getMonth()]}`;
  }
}

function toIsoDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}
